// Tank Levels — how much each tank holds, what went in and came out.
// Server: src/api-inventory/tank-levels.js.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { Tank } from './useMaterials';

export interface TankLevel {
  tankId: string;
  code: string;
  name: string;
  group: Tank['group'];
  groupLabel: string;
  capacityKl: number | null;
  product: string;
  levelKl: number | null;
  fillPct: number | null;
  spaceKl: number | null;
  belowZero: boolean;
  hasDip: boolean;
  lastDip: { dateKey: string; kl: number; raw: string; byName: string } | null;
  /** Newest Tank Register entry, with or without a KL ("sn70" alone). */
  latestEntry: { dateKey: string; raw: string; status: string; kl: number | null; byName: string } | null;
  inSinceDipKl: number;
  outSinceDipKl: number;
  lastVarianceKl: number | null;
  estimated: boolean;
  todayInKl: number;
  todayOutKl: number;
  lastActivityAt: string | null;
}

export interface TankLevelsResponse {
  tanks: TankLevel[];
  products: { product: string; kl: number; tanks: string[] }[];
  totals: { levelKl: number; capacityKl: number; spaceKl: number; capacityMissing: number; noReading: number };
  reasons: Record<string, string>;
}

export interface TankHistoryRow {
  type: 'dip' | 'in' | 'out';
  dateKey: string;
  at: string;
  kl: number | null;
  levelAfter: number | null;
  variance: number | null;
  product: string;
  raw: string;
  reason: string;
  reasonLabel: string;
  referenceNumber: string;
  note: string;
  byName: string;
  quantity: number | null;
  unit: string;
  estimated: boolean;
}

export function useTankLevels() {
  return useQuery({
    queryKey: ['inventory', 'tank-levels'],
    refetchInterval: 60_000,
    queryFn: async () => (await api.get<TankLevelsResponse>('/api/inventory/tanks/levels')).data,
  });
}

export function useTankHistory(tankId: string | null) {
  return useQuery({
    queryKey: ['inventory', 'tank-levels', 'history', tankId],
    enabled: !!tankId,
    queryFn: async () => (await api.get<{ levelKl: number | null; rows: TankHistoryRow[] }>(`/api/inventory/tanks/levels/${tankId}/history`)).data,
  });
}

export interface TankMovementInput {
  tankId: string;
  direction: 'in' | 'out' | 'transfer';
  quantity: number;
  unit: 'KL' | 'LTR' | 'KG';
  density?: number;
  product?: string;
  note?: string;
  date?: string;
  toTankId?: string;
}

/** Add oil, take it out, or move it to another tank; and record a dip. */
export function useTankActions() {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['inventory', 'tank-levels'] });
    void qc.invalidateQueries({ queryKey: ['inventory', 'tank-register'] });
  };
  const movement = useMutation({
    mutationFn: async ({ tankId, ...body }: TankMovementInput) => (await api.post(`/api/inventory/tanks/levels/${tankId}/movement`, body)).data,
    onSuccess: refresh,
  });
  const dip = useMutation({
    mutationFn: async ({ tankId, ...body }: { tankId: string; kl: number; product?: string; date?: string }) => (
      await api.post<{ levelKl: number | null; varianceKl: number | null; text: string }>(`/api/inventory/tanks/levels/${tankId}/dip`, body)
    ).data,
    onSuccess: refresh,
  });
  return { movement, dip };
}
